import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { downloadConversationJson, downloadConversationPdf } from '../utils/exportConversation';
import FeedbackModal from './FeedbackModal';
import './Sidebar.css';

function initials(name = '') {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

export default function Sidebar({
  user,
  conversations,
  activeId,
  onSelect,
  onCreate,
  onDelete,
  onLogout,
  collapsed,
  onToggleCollapse,
}) {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [exportMenuFor, setExportMenuFor] = useState(null);
  const menuRef = useRef(null);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    if (!exportMenuFor) return;
    function handleClickOutside(e) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setExportMenuFor(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportMenuFor]);

  function handleExport(conversation, format) {
    setExportMenuFor(null);
    if (format === 'json') downloadConversationJson(conversation);
    else downloadConversationPdf(conversation);
  }

  function openPanel(panel) {
    setMenuOpen(false);
    if (panel === 'billing') {
      navigate('/billing');
      return;
    }
    if (panel === 'account') {
      navigate('/account');
      return;
    }
    if (panel === 'settings') {
      navigate('/settings');
      return;
    }
    if (panel === 'users') {
      navigate('/users');
      return;
    }
    setActivePanel(panel);
  }

  const canManageUsers = user?.role === 1 && user?.userType === 2;
  const canManageBilling = !(user?.userType === 2 && user?.role === 2);

  return (
    <>
      <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-mark">P</div>
            <span className="sidebar-brand-name">Piua AI</span>
          </div>
          <div className="sidebar-header-actions">
            <button
              className="icon-btn"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
                  <path
                    d="M12 2V4M12 20V22M4 12H2M22 12H20M5.6 5.6L4.2 4.2M19.8 19.8L18.4 18.4M5.6 18.4L4.2 19.8M19.8 4.2L18.4 5.6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
            <button className="icon-btn" onClick={onToggleCollapse} title="Collapse sidebar">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 5H20M4 12H20M4 19H20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <button className="new-chat-btn" onClick={onCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
          New chat
        </button>

        <div className="conversation-list">
          {conversations.map((c) => (
            <div
              key={c.id}
              className={`conversation-item ${c.id === activeId ? 'conversation-item--active' : ''}`}
              onClick={() => onSelect(c.id)}
            >
              <span className="conversation-title">{c.title || 'New chat'}</span>
              <div className="conversation-actions">
                <div className="conversation-export-anchor" ref={exportMenuFor === c.id ? exportMenuRef : null}>
                  <button
                    className="conversation-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExportMenuFor((prev) => (prev === c.id ? null : c.id));
                    }}
                    title="Export conversation"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 4V15M12 15L8 11M12 15L16 11M5 19H19"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {exportMenuFor === c.id && (
                    <div className="export-menu" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleExport(c, 'json')}>Export as JSON</button>
                      <button onClick={() => handleExport(c, 'pdf')}>Export as PDF</button>
                    </div>
                  )}
                </div>
                <button
                  className="conversation-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(c.id);
                  }}
                  title="Delete conversation"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 6L18 18M6 18L18 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <div className="user-menu-anchor" ref={menuRef}>
            <button className="user-avatar" onClick={() => setMenuOpen((v) => !v)} title="Account menu">
              {initials(user?.fullname) || '?'}
            </button>
            {menuOpen && (
              <div className="user-menu">
                {canManageBilling && <button onClick={() => openPanel('billing')}>Billing</button>}
                <button onClick={() => openPanel('account')}>My Account</button>
                <button onClick={() => openPanel('settings')}>Settings</button>
                {canManageUsers && <button onClick={() => openPanel('users')}>Users</button>}
                <button onClick={() => openPanel('feedback')}>Feedback</button>
              </div>
            )}
          </div>
          <div className="user-meta">
            <div className="user-name">{user?.entity ? user.entity.name : user?.fullname}</div>
            <div className="user-plan">
              {user?.packageName || 'No plan'}
              {user?.entity ? ` · ${user.fullname}` : ''}
            </div>
          </div>
          <button className="icon-btn" onClick={onLogout} title="Sign out">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 17L20 12L15 7M20 12H9M9 4H6C4.9 4 4 4.9 4 6V18C4 19.1 4.9 20 6 20H9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </aside>

      {activePanel === 'feedback' && <FeedbackModal onClose={() => setActivePanel(null)} />}
    </>
  );
}
